import {Fragment, useCallback, useState} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {Dispatch} from 'redux';

import {createLogger} from '~/shared/logging';
import {selectCheckoutPayments, selectOrderError, selectUserData} from '~/shared/store/selectors';
import {getCurrentLocation, pushRoute} from '~/shared/router';
import {routeNames} from '~/shared/routes';
import actions from '~/shared/store/actions';
import {flexColumn} from '~/shared/theme/FlexLayout';
import OtlSectionsContainer from '~/shared/components/Otl/OtlSectionsContainer';
import {useOnPageInit} from '~/shared/managers/initManager/initManager';
import store from '~/shared/store';
import {media} from '~/shared/theme/utils';
import {OtlMode} from '~/shared/components/Otl/types';

const ModalRoot = styled.div<{isAddCreditCardMode?: boolean}>`
  background-color: ${({theme}) => theme.loginModal.backgroundColor};
  border-radius: 2px;
  overflow: hidden;
  ${flexColumn}
  ${media.minLargeMobile`
    && {
      width: 500px;
    }
  `}
`;

const logger = createLogger('OtlPageComponent');

const pushRouteToCheckout = () => {
  pushRoute('/checkout');
};

const handleOnClose = (dispatch: Dispatch, isModal: boolean, closeModal?: () => void, returnToModal?: () => void) => () => {
  if (!isModal) {
    pushRouteToCheckout();
  } else {
    const {routeName} = getCurrentLocation();
    if (closeModal && routeName === routeNames.checkout) {
      closeModal();
    } else {
      if (returnToModal) {
        dispatch(actions.setCurrentModal(returnToModal));
      }
    }
  }
};

const OtlPageComponent = ({closeModal, args}: {
  closeModal?: () => void;
  args?: Record<string, () => void>;
}) => {
  const orderError = useSelector(selectOrderError);
  const payments = useSelector(selectCheckoutPayments);
  const dispatch = useDispatch();
  const [isAddCreditCardMode, setIsCreditCardMode] = useState(false);
  const onModeChange = useCallback((nextMode: OtlMode) => {
    if (nextMode === OtlMode.ADD_CREDIT_CARD) {
      setIsCreditCardMode(true);
    } else {
      setIsCreditCardMode(false);
    }
  }, []);

  const encryptedMoneycardId = payments?.find(p =>
    p.cardId === orderError?.data.submitOrderFailurePaymentId,
  )?.encryptedMoneycardId;

  const isModal = !!closeModal;

  useOnPageInit(() => {
    const userData = selectUserData(store.getState());
    if (!userData) {
      if (isModal) {
        closeModal();
        logger.error('Modal closed, no userData');
      } else {
        pushRoute(`/signin?returnUrl=${encodeURIComponent('/otl')}`);
        logger.warn('Redirecting to login and onSuccess should redirect back to the otl page');
      }
    }
  });

  const RootElement = closeModal ? ModalRoot : Fragment;

  return (
    <RootElement isAddCreditCardMode={isAddCreditCardMode}>
      <OtlSectionsContainer
        orderCardId={encryptedMoneycardId}
        onClose={handleOnClose(dispatch, isModal, closeModal, args?.returnToModal)}
        onOtlSuccess={handleOnClose(dispatch, isModal, closeModal, args?.returnToModal)}
        onModeChange={onModeChange}
      />
    </RootElement>
  );
};

export default OtlPageComponent;
